import React, { useState, type CSSProperties } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useActiveAthlete } from "@/hooks/useActiveAthlete";
import { useAuth } from "@/hooks/useAuth";
import StatsIntegration from "@/components/StatsIntegration";
import { 
  LineChart, 
  TrendingUp,
  Layers,
  Activity,
  UserPlus,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";

const legacyTheme: CSSProperties = {
  "--background": "220 58% 9%",
  "--foreground": "0 0% 100%",
  "--card": "221 45% 14%",
  "--card-foreground": "0 0% 100%",
  "--muted": "221 35% 18%",
  "--muted-foreground": "215 20% 80%",
  "--border": "40 70% 55%",
  "--input": "221 25% 20%",
  "--ring": "40 70% 55%",
};

const StatsPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = React.useState("overview");
  const [athleteForm, setAthleteForm] = useState({
    name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
    sports: "",
    position: "",
    graduationYear: new Date().getFullYear() + 4,
    gpa: 0.0,
    school: "",
    bio: ""
  });
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);

  // Active athlete (shared across the app; switchable)
  const { athlete, isLoading: isLoadingAthlete } = useActiveAthlete();

  // Create athlete profile mutation
  const createAthleteMutation = useMutation({
    mutationFn: async (athleteData: typeof athleteForm) => {
      const response = await fetch("/api/athletes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...athleteData, userId: user?.id }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create athlete profile");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Profile Created",
        description: "Your athlete profile has been created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes"] });
      setIsCreatingProfile(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create athlete profile",
        variant: "destructive",
      });
    },
  });

  const handleCreateProfile = (e: React.FormEvent) => {
    e.preventDefault();
    createAthleteMutation.mutate(athleteForm);
  };

  const athleteId = athlete?.id || "";

  // Handle the case when no athlete profile exists
  if (!athleteId && !isCreatingProfile) {
    return (
      <div className="container mx-auto min-h-screen px-4 py-8 text-white" style={legacyTheme}>
        <div className="mb-8 rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] p-6 text-center shadow-lg shadow-black/25">
          <h1 className="text-2xl font-bold mb-4">Sports Statistics</h1>
          <p className="text-muted-foreground mb-6">
            No athlete profile found. Create one to start tracking your sports statistics.
          </p>
          <Button 
            onClick={() => setIsCreatingProfile(true)}
            className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Create Athlete Profile
          </Button>
        </div>
      </div>
    );
  }

  // Show athlete profile creation form
  if (isCreatingProfile) {
    return (
      <div className="container mx-auto min-h-screen max-w-3xl px-4 py-8 text-white" style={legacyTheme}>
        <div className="mb-8 rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] p-6 text-center shadow-lg shadow-black/25">
          <h1 className="text-2xl font-bold">Create Athlete Profile</h1>
          <p className="text-muted-foreground mt-1">
            Set up your profile to start tracking sports statistics
          </p>
        </div>
        
        <form onSubmit={handleCreateProfile} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={athleteForm.name}
                onChange={(e) => setAthleteForm({ ...athleteForm, name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sports">Sports (comma separated)</Label>
              <Input
                id="sports"
                placeholder="e.g., Baseball, Basketball"
                value={athleteForm.sports}
                onChange={(e) => setAthleteForm({ ...athleteForm, sports: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <Input
                id="position"
                placeholder="e.g., Pitcher, Forward"
                value={athleteForm.position}
                onChange={(e) => setAthleteForm({ ...athleteForm, position: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="school">School Name</Label>
              <Input
                id="school"
                value={athleteForm.school}
                onChange={(e) => setAthleteForm({ ...athleteForm, school: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="graduationYear">Graduation Year</Label>
              <Input
                id="graduationYear"
                type="number"
                min={new Date().getFullYear()}
                max={new Date().getFullYear() + 10}
                value={athleteForm.graduationYear}
                onChange={(e) => setAthleteForm({ ...athleteForm, graduationYear: parseInt(e.target.value) })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="gpa">GPA</Label>
              <Input
                id="gpa"
                type="number"
                step="0.01"
                min="0"
                max="5.0"
                placeholder="e.g., 3.5"
                value={athleteForm.gpa}
                onChange={(e) => setAthleteForm({ ...athleteForm, gpa: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bio">Biography/Description</Label>
            <textarea
              id="bio"
              className="flex min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Brief description about the athlete"
              value={athleteForm.bio}
              onChange={(e) => setAthleteForm({ ...athleteForm, bio: e.target.value })}
            />
          </div>
          
          <div className="flex justify-end space-x-4 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsCreatingProfile(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
              disabled={createAthleteMutation.isPending}
            >
              {createAthleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Profile"
              )}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="container mx-auto min-h-screen px-4 py-8 text-white" style={legacyTheme}>
      <div className="mb-8 flex flex-col gap-4 rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-5 py-5 shadow-lg shadow-black/25 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Sports Statistics</h1>
          <p className="text-muted-foreground mt-1">
            Track your performance statistics across platforms and seasons
          </p>
        </div>

        <div className="mt-4 md:mt-0">
          <Button className="bg-primary hover:bg-primary/90 text-white flex items-center">
            <LineChart className="mr-2 h-4 w-4" />
            Generate Stats Report
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 w-full max-w-md mb-8">
          <TabsTrigger value="overview" className="flex items-center">
            <Activity className="mr-2 h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="platforms" className="flex items-center">
            <Layers className="mr-2 h-4 w-4" />
            Platforms
          </TabsTrigger>
          <TabsTrigger value="trends" className="flex items-center">
            <TrendingUp className="mr-2 h-4 w-4" />
            Trends
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Recent Games</CardTitle>
                <CardDescription>
                  Your latest game performances
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Connect a stats platform to see your recent game statistics.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Season Overview</CardTitle>
                <CardDescription>
                  Current season statistics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Connect a stats platform to see your season-by-season performance.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Performance Highlights</CardTitle>
                <CardDescription>
                  Your standout achievements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Connect a stats platform to generate performance highlights.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="platforms" className="space-y-6">
          <StatsIntegration athleteId={athleteId} />
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Performance Trends</CardTitle>
              <CardDescription>
                Analyze your statistics over time to identify trends and areas for improvement
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px] flex items-center justify-center border rounded-md bg-muted/20">
                <p className="text-muted-foreground">
                  Connect a stats platform and sync your data to see performance trends.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StatsPage;